import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import type {
	IAssemblyServicesData,
	ILocalizedInformation,
	ITheme
} from '@inter-ikea-kompis/types';
import type { RenderResult } from '@inter-ikea-kompis/web-component';

import { FocusRing, Hyperlink } from '@ingka/common-styles-webc';
import type { StatusColour, StatusPosition } from '@ingka/status-webc';
import { Status as SkapaStatus } from '@ingka/status-webc/passive.js';

import type IAssemblyServicesTranslations from '../../types/IAssemblyServicesTranslations.js';
import AssemblyServicesStatusButtonStyle from './AssemblyServicesStatusButtonStyle.js';

/**
 * @event statusButtonClick User clicks on the assembly services status button.
 */

export default class AssemblyServicesStatusButton extends KompisElement {
	// Theme.
	@property({ type: Object, required: true })
	public theme!: ITheme;

	// Assembly services data, containing if assembly services are available or not.
	@property({ type: Object })
	public assemblyServicesData: IAssemblyServicesData | null = null;

	// Localized information. Deprecated - use "translations" instead.
	@property({ type: Object })
	public localizedInformation: ILocalizedInformation | null = null;

	// The translations retrieved from translations service.
	@property({ type: Object })
	public translations: IAssemblyServicesTranslations | null = null;

	// Submitted and validated ZIP code.
	@property({ type: String })
	public zipCode: string | null = null;

	public override async connectedCallback(): Promise<void> {
		this.customElements.define('skapa-status', SkapaStatus);

		super.connectedCallback();
	}

	public override render(): RenderResult {
		// We want translations to be required, but since both props to provide translations are optional, this check is needed until localizedInformation is removed.
		if (!this.hasTranslations()) {
			// We can't throw an error because on the first render no props are set.
			return null;
		}

		return html`
			<style>
				${Hyperlink.CSS + FocusRing.CSS}
				${AssemblyServicesStatusButtonStyle.getStyle({ theme: this.theme })}
			</style>

			${this.getContent()}
		`;
	}

	private hasTranslations(): boolean {
		return !!this.localizedInformation || !!this.translations;
	}

	private getTranslations(): IAssemblyServicesTranslations {
		const translations = this.translations
			? this.translations
			: this.localizedInformation?.translations;

		return <IAssemblyServicesTranslations>translations;
	}

	private getContent(): RenderResult {
		const position: StatusPosition = 'leading';
		const color = this.getStatusIndicatorColor();

		// Status needs to be the same element between states for focus to be put back when setting or forgetting a zip code.
		return html`<skapa-status
			colour="${color}"
			class="${color ? '' : 'status-hidden'}"
			position="${position}"
			><span>${this.getLabel()}</span></skapa-status
		>`;
	}

	private getStatusIndicatorColor(): StatusColour | null {
		if (!this.zipCode || !this.assemblyServicesData) {
			return null;
		}

		return this.assemblyServicesData?.valid ? 'green' : 'red';
	}

	private getLabel(): RenderResult {
		return html`${this.getAvailabilityText()}${this.getButton()}`;
	}

	private getAvailabilityText(): string | null {
		const { assemblyServicesStatusAvailableZip, assemblyServicesStatusUnavailableZip } =
			this.getTranslations();

		if (!this.zipCode || !this.assemblyServicesData) {
			return null;
		}

		if (this.assemblyServicesData.valid) {
			return assemblyServicesStatusAvailableZip.replace('{{zip_code}}', '');
		}

		return assemblyServicesStatusUnavailableZip.replace('{{zip_code}}', '');
	}

	private getButton(): RenderResult {
		return html`<button
			class="buttonAsLinkResets ${Hyperlink.classnames()} buttonAsLinkStyling"
			@click="${() => {
				this.dispatchEvent(new CustomEvent('statusButtonClick', { bubbles: true }));
			}}"
		>
			<span
				>${this.zipCode && this.assemblyServicesData
					? this.zipCode
					: this.getTranslations().assemblyServicesStatusDefault}</span
			>
		</button>`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'skapa-status': SkapaStatus;
		'kompis-assembly-services-status-button': AssemblyServicesStatusButton;
	}
}
